import { useRef, useCallback, useReducer } from 'react';
import { useTranslation } from 'react-i18next';

import { useConfig } from '../../../config/ConfigProvider';
import { captureException } from '../../../services/sentryService';
import { CreditCardBrand, CustomerType } from '../../../types';
import AdyenCreditCardForm, { AdyenCreditCardFormRef } from '../../Adyen/Forms/AdyenCreditCardForm';
import { AdyenEnvironment } from '../../Adyen/Forms/AdyenCreditCardForm.static';
import HtmlEmbeddedAlert from '../../Banners/HtmlEmbeddedAlert';
import { useCreditcardService } from '../../CreditCardsMutationProvider';
import ErrorAlert from '../../error/ErrorAlert';
import BaseModal from '../../Overlays/Modals/BaseModal';
import { OverlayIds } from '../../OverlaysProvider';

export interface NewAdyenCreditCardModalProps {
  adyenEnvironment: AdyenEnvironment;
  supportedCreditCardBrands: CreditCardBrand[];
  customerType: CustomerType;
  isFallbackPsp: boolean;
}

interface NewAdyenCreditCardModalState {
  errorMessage: string | null;
  addCardLoading: boolean;
  showForm: boolean;
}

const INITIAL_STATE: NewAdyenCreditCardModalState = {
  errorMessage: null,
  addCardLoading: false,
  showForm: true,
};

type NewAdyenCreditCardModalAction =
  | {
      type: 'load';
    }
  | {
      type: 'error';
      errorMessage: string;
      showForm?: boolean;
    };

function reduce(
  state: NewAdyenCreditCardModalState,
  action: NewAdyenCreditCardModalAction,
): NewAdyenCreditCardModalState {
  switch (action.type) {
    case 'load':
      return { showForm: true, addCardLoading: true, errorMessage: null };
    case 'error':
      return {
        // we default to showForm=true if hideForm is not given
        showForm: typeof action.showForm === 'undefined' ? true : action.showForm,
        addCardLoading: false,
        errorMessage: action.errorMessage,
      };
    default:
      // eslint-disable-next-line no-case-declarations
      const none: never = action;
      captureException({ message: `Cannot handle action ${none}` });
      return state;
  }
}

/**
 * Modal holding the Adyen credit card form.
 * Will save the credit card automatically when the modal is confirmed.
 */
const NewAdyenCreditCardModal: React.FC<NewAdyenCreditCardModalProps> = ({
  adyenEnvironment,
  supportedCreditCardBrands,
  customerType,
  isFallbackPsp,
}) => {
  const form = useRef<AdyenCreditCardFormRef>(null);
  const { config } = useConfig();
  const { saveAdyenCreditCard } = useCreditcardService();
  const { t } = useTranslation();
  const [state, dispatch] = useReducer(reduce, INITIAL_STATE);

  const handleSubmit = () => {
    if (form.current === null) {
      dispatch({
        type: 'error',
        errorMessage: t('checkout.payment.creditcard.error.addcard.apicall'),
      });
    } else {
      const adyenFormData = form.current.computeAdyenCreditCardFormData();

      if (adyenFormData !== null) {
        dispatch({ type: 'load' });

        saveAdyenCreditCard(adyenFormData)
          .then((isSuccess) => {
            if (isSuccess) {
              if (config.env !== 'local') {
                window.location.assign('/checkout/preview');
              }
            } else {
              dispatch({
                type: 'error',
                errorMessage: t('checkout.payment.creditcard.error.addcard.apicall'),
              });
            }
          })
          .catch((error: unknown) => {
            // on any unexpected error, send it to Sentry and display a generic error message to the user
            captureException({ error });
            dispatch({
              type: 'error',
              errorMessage: t('checkout.payment.creditcard.error.addcard.apicall'),
            });
          });
      }
    }
  };

  const onFatalError = useCallback(
    (error: unknown, showForm?: boolean) => {
      captureException({ error });
      dispatch({
        type: 'error',
        errorMessage: t('checkout.payment.creditcard.error.addcard.apicall'),
        showForm,
      });
    },
    [dispatch],
  );

  return (
    <BaseModal
      modalId={OverlayIds.ADD_NEW_CARD}
      handleSubmit={handleSubmit}
      heading={t('checkout.payment.creditcard.addnew')}
      primaryButtonLabel={t('general.button.next')}
      headingId="add-card"
      closeBtnAriaLabel="Close"
      dataZta="modal-addCard"
      size="md"
      isLoading={state.addCardLoading}>
      <ErrorAlert errorMessage={state.errorMessage} />
      <HtmlEmbeddedAlert innerHtml={t('checkout.payment.creditcard.3d.secure.message')} />
      {state.showForm && (
        <AdyenCreditCardForm
          ref={form}
          adyenEnvironment={adyenEnvironment}
          clientKey={config.adyen.clientKey}
          supportedCreditCardBrands={supportedCreditCardBrands}
          onFatalError={onFatalError}
          allowVaulting={!isFallbackPsp && customerType !== 'GUEST_CUSTOMER'}
          labelInline={true}
        />
      )}
    </BaseModal>
  );
};

export default NewAdyenCreditCardModal;
